import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import EmployeeLayout from "@/components/EmployeeLayout";
import AdminLayout from "@/components/AdminLayout";
import EmployeeDashboard from "@/pages/employee/EmployeeDashboard";
import MarkAttendance from "@/pages/employee/MarkAttendance";
import EmployeeCalendar from "@/pages/employee/EmployeeCalendar";
import EmployeeSalary from "@/pages/employee/EmployeeSalary";
import EmployeeLeaves from "@/pages/employee/EmployeeLeaves";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Employees from "@/pages/admin/Employees";
import Branches from "@/pages/admin/Branches";
import Holidays from "@/pages/admin/Holidays";
import Leaves from "@/pages/admin/Leaves";
import Advances from "@/pages/admin/Advances";
import Payroll from "@/pages/admin/Payroll";
import AttendanceAdmin from "@/pages/admin/AttendanceAdmin";
import Audit from "@/pages/admin/Audit";
import Settings from "@/pages/admin/Settings";
import Reports from "@/pages/admin/Reports";
import Reimbursements from "@/pages/admin/Reimbursements";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, refetchOnWindowFocus: false, retry: 1 } },
});

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="h-12 w-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route path="/app" element={<Protected role="employee"><EmployeeLayout /></Protected>}>
              <Route index element={<EmployeeDashboard />} />
              <Route path="mark" element={<MarkAttendance />} />
              <Route path="calendar" element={<EmployeeCalendar />} />
              <Route path="salary" element={<EmployeeSalary />} />
              <Route path="leaves" element={<EmployeeLeaves />} />
            </Route>

            <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="employees" element={<Employees />} />
              <Route path="branches" element={<Branches />} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="leaves" element={<Leaves />} />
              <Route path="advances" element={<Advances />} />
              <Route path="reimbursements" element={<Reimbursements />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="reports" element={<Reports />} />
              <Route path="attendance" element={<AttendanceAdmin />} />
              <Route path="audit" element={<Audit />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
